// PDF / image preview pane used inside the OCR Review page.
// Extracted from pages/InvoiceOCR.jsx.
import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  Loader2,
  AlertCircle,
  FileText,
  Download,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DocumentViewer = ({ jobId, filename, contentType }) => {
  const [zoom, setZoom] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!jobId) return;

    const loadDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiFetch(`${API_URL}/api/ocr/${jobId}/original`);

        if (!response.ok) {
          throw new Error('Failed to load document');
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setObjectUrl(url);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDocument();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const isPdf = contentType?.includes('pdf') || filename?.toLowerCase().endsWith('.pdf');
  const isImage = contentType?.includes('image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(filename || '');

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-2 border-b bg-muted/30">
        <span className="text-sm text-muted-foreground truncate max-w-[200px]" title={filename}>
          {filename}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setZoom(z => Math.max(25, z - 25))}
            disabled={zoom <= 25}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs w-12 text-center">{zoom}%</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setZoom(z => Math.min(200, z + 25))}
            disabled={zoom >= 200}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Separator orientation="vertical" className="h-6 mx-1" />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => window.open(`${API_URL}/api/ocr/${jobId}/original`, '_blank')}
            title="Download original"
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Viewer */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-muted/10 flex items-start justify-center p-4"
      >
        {loading && (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground mt-2">Loading document...</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <AlertCircle className="w-10 h-10 text-destructive mb-2" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {!loading && !error && objectUrl && (
          <>
            {isPdf && (
              // Native browser PDF viewer responds to #zoom=N (Chrome,
              // Edge) and #zoom=N&view=Fit (Firefox/pdf.js). Setting the
              // iframe's width to a percentage only widens the frame —
              // it doesn't scale the PDF *content*, which is why the
              // zoom buttons appeared broken. Append the fragment on
              // every render so the viewer reflows when zoom changes.
              <iframe
                src={`${objectUrl}#zoom=${zoom}&view=FitH`}
                key={zoom}
                className="border-0 shadow-lg bg-white"
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '500px',
                }}
                title="PDF Document"
              />
            )}
            {isImage && (
              <img
                src={objectUrl}
                alt="Document"
                className="shadow-lg"
                style={{
                  width: `${zoom}%`,
                  maxWidth: 'none',
                  height: 'auto'
                }}
              />
            )}
            {!isPdf && !isImage && (
              <div className="text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Preview not available</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => window.open(`${API_URL}/api/ocr/${jobId}/original`, '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download File
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
